import {
  Box, Button, Flex, Text, Title
} from '@huspy/briks-web';
import { Image } from '@mantine/core';
import { imagesPath } from '@shared/css.const';
import { useTranslation } from 'react-i18next';
import { BaseSlido } from '@modules/core/components/BaseSlido';
import { useDisclosure } from '@mantine/hooks';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import { AMPLITUDE_FEATURE_FLAGS } from '@modules/core/api/types';
import { BuyCreditsSlido } from '@modules/payments/presentation/features/BuyCreditsSlido';
import { PaymentCallBackSlido } from '@modules/payments/presentation/features/PaymentCallbackSlido';
import { useEffect, useMemo } from 'react';
import creditsTopSectionStyles from '@modules/core/presentation/v2/pages/Credits/features/TopSection/styles';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { USER_EVENTS } from '@shared/analytics/events';
import { useGlobalStore } from '@modules/core/store';

const { container, availableCreditsContainer, creditsIconContainer } = creditsTopSectionStyles;

type Props = {
  availableCredits?: number;
  isGetMoreCreditsAvailable?: boolean;
};

const MoreCreditsFormSlido = ({ opened, close }: { opened: boolean, close: () => void }) => {
  useEffect(() => {
    if (opened) {
      trackAmplitudeEvent(USER_EVENTS.CREDITS.PURCHASE_PAGE_VIEWED);
    }
  }, [opened]);
  return (
    <BaseSlido
      opened={ opened }
      close={ close }
      title={ (<></>) }
    >
      <iframe
        title='Buy credits'
        src={ CREDITS_BUY_FORM_EMBED_URL }
        style={ {
          width: '100%',
          height: '100%',
        } }
      />
    </BaseSlido>
  );
};

const CreditsTopSection = ({ availableCredits, isGetMoreCreditsAvailable }: Props) => {
  const { t } = useTranslation();
  const { featureFlags } = useFeatureFlags();

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);

  const { credits, setIsOpenedBuyCredits } = useGlobalStore();

  const [isPaymentCallbackSlidoOpen, {
    open: openPaymentCallback,
    close: closePaymentCallback,
  }] = useDisclosure(false);

  useEffect(() => {
    const paymentFlow = searchParams.get('payment_flow') === 'true';
    const message = searchParams.get('message') ?? '';
    const credits = Number.parseInt(searchParams.get('credits') ?? '0', 10);
    const quantity = 1;
    if (!paymentFlow || credits <= 0) {
      return;
    }
    if (['success'].includes(message)) {
      openPaymentCallback();
      trackAmplitudeEvent(
        USER_EVENTS.CREDITS.PURCHASE_COMPLETED,
        { credit_quantity: credits, credit_bundle_quantity: quantity }
      );
    } else if (['cancel'].includes(message)) {
      openPaymentCallback();
      trackAmplitudeEvent(
        USER_EVENTS.CREDITS.PURCHASE_CANCELED,
        { credit_quantity: credits, credit_bundle_quantity: quantity }
      );
    } else if (['user_cancel'].includes(message)) {
      trackAmplitudeEvent(
        USER_EVENTS.CREDITS.PURCHASE_CANCELED,
        { credit_quantity: credits, credit_bundle_quantity: quantity }
      );
    }
  }, [searchParams, openPaymentCallback]);

  return (
    <>
      { featureFlags?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_PAYMENT_GATEWAY]
        ? (
          <>
            <PaymentCallBackSlido
              isOpen={ isPaymentCallbackSlidoOpen }
              close={ closePaymentCallback }
              openBuyCreditsSlido={ () => setIsOpenedBuyCredits(true) }
              message={ searchParams.get('message') ?? '' }
              credits={ Number.parseInt(searchParams.get('credits') ?? '0', 10) }
            />

            <BuyCreditsSlido />
          </>
        )
        : (
          <MoreCreditsFormSlido
            opened={ credits.isOpenedBuyCredits }
            close={ () => setIsOpenedBuyCredits(false) }
          />
        )}

      <Flex justify='space-between' className={ container }>
        <Flex direction='column'>
          <Text size='lg' color='text-primary_on-brand'>{t('core.credits.availableCredits')}</Text>

          <Box className={ availableCreditsContainer }>
            <Title size='h5' weight='semiBold' color='text-primary_on-brand' data-test='available-credits-count'>
              {availableCredits ?? '??'}
            </Title>

            <Box className={ creditsIconContainer }>
              <Image w={ 24 } src={ `${imagesPath}/credits.png` } />
            </Box>
          </Box>
        </Flex>

        {isGetMoreCreditsAvailable ? (
          <Flex align='center'>
            <Button variant='primary' size='md' onClick={ () => setIsOpenedBuyCredits(true) }>
              {t('core.credits.getMoreCredits')}
            </Button>
          </Flex>
        ) : null}
      </Flex>
    </>
  );
};

export default CreditsTopSection;
